import React, { useState, useMemo } from 'react';
import { SpeedTestResult, ConnectionInfo, FeedbackData } from '../types';
import { submitFeedback } from '../services/feedbackService';
import { StarIcon } from 'shared-icons';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectionInfo: ConnectionInfo | null;
  results: SpeedTestResult;
  latestTestId: number | null;
}

const getQuestionForCurrentTime = (): string => {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 12) return "How is your internet speed for work or school this morning?";
  if (hour >= 12 && hour < 17) return "Is your connection stable for video calls or browsing this afternoon?";
  if (hour >= 17 && hour < 22) return "How is your experience with streaming or gaming on your connection tonight?";
  return "Is your connection performing as expected during these off-peak hours?";
};

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, connectionInfo, results, latestTestId }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<{ type: 'submitting' | 'error' | 'success'; message: string } | null>(null);
  const question = useMemo(() => getQuestionForCurrentTime(), [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (status?.type === 'submitting') return;
    setRating(0); setHoverRating(0); setComment(''); setStatus(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) { setStatus({ type: 'error', message: 'Please select a rating before submitting.' }); return; }
    setStatus({ type: 'submitting', message: 'Submitting...' });
    const feedbackData: FeedbackData = { question, rating, comment, timestamp: new Date().toISOString(), connectionInfo, results, testHistoryId: latestTestId };
    const result = await submitFeedback(feedbackData);
    if (result.success) { setStatus({ type: 'success', message: result.message }); setTimeout(handleClose, 2000); }
    else { setStatus({ type: 'error', message: result.message }); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={handleClose} role="dialog" aria-modal="true" aria-labelledby="feedback-title" style={{ animation: 'modal-fade-in 0.3s' }}>
      <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-xl w-full max-w-md p-6" onClick={(e)=>e.stopPropagation()} style={{ animation: 'modal-content-pop 0.3s' }}>
        <div className="flex justify-between items-center mb-4"><h2 id="feedback-title" className="text-xl font-bold text-slate-900 dark:text-white">Provide Feedback</h2><button onClick={handleClose} className="text-gray-400 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white text-2xl leading-none" aria-label="Close modal">&times;</button></div>
        <form onSubmit={handleSubmit}>
            <p className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">{question}</p>
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-4">Your feedback helps everyone understand ISP performance for {connectionInfo?.isp || 'your ISP'}.</p>
            <div className="flex justify-center items-center my-4 space-x-1" aria-label="Star rating">
              {[1,2,3,4,5].map(star => (<button type="button" key={star} onClick={()=>setRating(star)} onMouseEnter={()=>setHoverRating(star)} onMouseLeave={()=>setHoverRating(0)} className="text-yellow-400 focus:outline-none transition-transform duration-150 transform hover:scale-125 focus:scale-125" aria-label={`Rate ${star} out of 5 stars`} aria-pressed={rating===star}><StarIcon width={32} height={32} filled={(hoverRating||rating)>=star}/></button>))}
            </div>
            <textarea value={comment} onChange={(e)=>setComment(e.target.value)} placeholder="Add an optional comment..." rows={3} className="w-full bg-gray-100 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md p-2 text-slate-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:outline-none resize-none" aria-label="Optional comment" disabled={status?.type==='submitting'||status?.type==='success'}/>
            <div className="mt-4 h-6">{status&&<div className={`text-sm text-center p-1 rounded-md ${status.type==='error'?'text-red-700 dark:text-red-300':status.type==='success'?'text-green-700 dark:text-green-300':'text-slate-600 dark:text-slate-400'}`}>{status.message}</div>}</div>
            <div className="mt-2 flex justify-end gap-4">
                <button type="button" onClick={handleClose} disabled={status?.type==='submitting'} className="px-4 py-2 bg-gray-200 dark:bg-slate-700/50 text-slate-800 dark:text-white font-semibold rounded-full hover:bg-gray-300 dark:hover:bg-slate-600/50 transition disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={rating===0||status?.type==='submitting'||status?.type==='success'} className="px-4 py-2 bg-cyan-600 text-white font-semibold rounded-full hover:bg-cyan-500 transition disabled:opacity-50 disabled:cursor-not-allowed">{status?.type==='submitting'?'Submitting...':'Submit'}</button>
            </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;
